import React from "react";
import { Link } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Fade, Grow, InputAdornment, TextField, Typography } from "@mui/material";
import {
    Email as EmailIcon,
    ArrowBack as ArrowBackIcon,
    CheckCircle as CheckCircleIcon,
    Send as SendIcon
} from '@mui/icons-material';

import { FormState, getErrorIcon, getErrorSeverity } from "./PasswordFunctions";
import { api } from "../../services/api";

type RenderEmailProps = {
    formState: FormState;
    setFormState: React.Dispatch<React.SetStateAction<FormState>>
}

const RenderEmailStep = ({ formState, setFormState }: RenderEmailProps) => {
    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const email = event.target.value;
        const isValid = validateEmail(email);

        setFormState(prev => ({
            ...prev,
            email,
            isEmailValid: isValid,
            error: null,
            errorType: null,
        }));
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!formState.isEmailValid) {
            setFormState(prev => ({
                ...prev,
                error: 'Por favor, insira um email válido',
                errorType: 'validation',
            }));
            return;
        }

        setFormState(prev => ({
            ...prev,
            isLoading: true,
            error: null,
            errorType: null,
        }));

        try {
            const response = await api.post(`/recuperarsenha`, { email_usuario: formState.email });
            if (response.data.success) {
                setFormState(prev => ({
                    ...prev,
                    isLoading: false,
                    isCodeSent: true,
                    resendCooldown: 60,
                }));
            } else if (response.data.error) {
                setFormState(prev => ({
                    ...prev,
                    isLoading: false,
                    error: response.data.msg,
                    errorType: 'not-found',
                }));
            }
        } catch(error) {
            console.log(error);
            setFormState(prev => ({
                ...prev,
                isLoading: false,
                error: 'Erro interno do servidor. Tente novamente mais tarde.',
                errorType: 'server',
            }));
        }
    };

    return (
        <Grow in timeout={600}>
            <Box component="form" onSubmit={handleSubmit}>
                <TextField
                    fullWidth
                    type="email"
                    label="Email"
                    variant="outlined"
                    value={formState.email}
                    onChange={handleEmailChange}
                    error={!!formState.error && formState.errorType === 'validation'}
                    helperText={
                        formState.error && formState.errorType === 'validation'
                            ? formState.error
                            : formState.email && formState.isEmailValid
                                ? 'Email válido'
                                : ''
                    }
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <EmailIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: formState.email && formState.isEmailValid && (
                            <InputAdornment position="end">
                                <CheckCircleIcon color="success" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            },
                            '&.Mui-focused': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                            },
                        },
                    }}
                />

                {formState.error && formState.errorType !== 'validation' && (
                    <Fade in>
                        <Alert
                            severity={getErrorSeverity(formState)}
                            icon={getErrorIcon(formState)}
                            sx={{
                                mb: 3,
                                borderRadius: 2,
                                '& .MuiAlert-icon': {
                                    fontSize: 20,
                                },
                            }}
                        >
                            <Typography variant="body2" component="div">
                                {formState.error}
                            </Typography>
                        </Alert>
                    </Fade>
                )}

                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={!formState.isEmailValid || formState.isLoading}
                    startIcon={
                        formState.isLoading ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            <SendIcon />
                        )
                    }
                >
                    {formState.isLoading ? 'Verificando...' : 'Enviar Email'}
                </Button>

                <Link to="/login">
                    <Button
                        fullWidth
                        variant="text"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            mt: 2,
                            py: 1,
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(33, 150, 243, 0.04)',
                                transform: 'translateX(-4px)',
                            },
                        }}
                    >
                        Voltar ao Login
                    </Button>
                </Link>
            </Box>
        </Grow>
    );
}

export default RenderEmailStep;